import type { CmmdContextPaths, CommandId, EnvironmentId } from "@cmmd-forge/contracts";

import { buildPrimaryFetchInit } from "../primary/fetchInit";
import { resolvePrimaryEnvironmentHttpUrl } from "../primary/target";

type ExecutionIntentPath = "/api/forge/context/runtimes/{runtimeId}/execution-intents";
type ExecutionIntentDispatchPath =
  "/api/forge/context/runtimes/{runtimeId}/execution-intents/{intentId}/dispatched";

type ExecutionIntentPost = NonNullable<CmmdContextPaths[ExecutionIntentPath]["post"]>;
type ExecutionIntentDispatchPost = NonNullable<
  CmmdContextPaths[ExecutionIntentDispatchPath]["post"]
>;

export type RuntimeExecutionIntentAction = NonNullable<
  ExecutionIntentPost["requestBody"]
>["content"]["application/json"]["action"];
export type RuntimeExecutionIntentData =
  ExecutionIntentPost["responses"][200]["content"]["application/json"]["data"];
export type RuntimeExecutionConnection = NonNullable<RuntimeExecutionIntentData["connection"]>;
/**
 * Reports a refusal the client will retry, with the typed reason code CMMD
 * sent. The code is what a caller renders from: `runtime_updating` is an
 * Environment Notice in its waiting state, never a failed send. The message is
 * CMMD's own prose and is only a fallback for surfaces that have no notice.
 */
export type RuntimeExecutionIntentWaitHandler = (message: string, code: string | null) => void;

export type RuntimeExecutionIntentDispatchData =
  ExecutionIntentDispatchPost["responses"][200]["content"]["application/json"]["data"];

interface RuntimeExecutionIntentRequest {
  readonly method: "POST";
  readonly path: string;
  readonly body: unknown;
  readonly signal: AbortSignal | undefined;
}

type RuntimeExecutionIntentRequestFn = <T>(request: RuntimeExecutionIntentRequest) => Promise<T>;

interface RuntimeExecutionIntentRequestDeps {
  readonly request: RuntimeExecutionIntentRequestFn;
  readonly sleep?: (delayMs: number, signal: AbortSignal | undefined) => Promise<void>;
}

const PREPARE_RETRY_DELAY_MS = 2_000;
const PREPARE_MAX_ATTEMPTS = 150;
const ACKNOWLEDGE_MAX_ATTEMPTS = 5;

class RuntimeExecutionIntentError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(input: {
    readonly message: string;
    readonly status: number;
    readonly code: string | null;
  }) {
    super(input.message);
    this.name = "RuntimeExecutionIntentError";
    this.status = input.status;
    this.code = input.code;
  }
}

function executionIntentPath(runtimeId: string): string {
  return `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/execution-intents`;
}

function executionIntentDispatchPath(input: {
  readonly runtimeId: string;
  readonly intentId: string;
}): string {
  return `${executionIntentPath(input.runtimeId)}/${encodeURIComponent(input.intentId)}/dispatched`;
}

async function defaultRequest<T>(request: RuntimeExecutionIntentRequest): Promise<T> {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl(request.path),
    buildPrimaryFetchInit({
      method: request.method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request.body),
      ...(request.signal ? { signal: request.signal } : {}),
    }),
  );
  const body = (await response.json().catch(() => ({}))) as {
    readonly error?: unknown;
    readonly code?: unknown;
  };
  if (!response.ok) {
    throw new RuntimeExecutionIntentError({
      message:
        typeof body.error === "string" && body.error.trim().length > 0
          ? body.error
          : `CMMD execution intent request failed with HTTP ${response.status}.`,
      status: response.status,
      code: typeof body.code === "string" ? body.code : null,
    });
  }
  return body as T;
}

function defaultSleep(delayMs: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }
    const handleAbort = () => {
      window.clearTimeout(timeoutId);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

function isRetryableRequestError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof RuntimeExecutionIntentError)) return false;
  if (error.status >= 500) return true;
  if (error.status !== 409) return false;
  return ![
    "environment_deleting",
    "environment_draining",
    "environment_paused",
    "execution_intent_idempotency_conflict",
    "checkout_registration_missing",
    "checkout_registration_changed",
  ].includes(error.code ?? "");
}

function terminalIntentMessage(data: RuntimeExecutionIntentData): string {
  return data.intent.failure?.message ?? "This Environment could not prepare the requested action.";
}

async function requestExecutionIntentAttempt(input: {
  readonly request: RuntimeExecutionIntentRequestFn;
  readonly runtimeId: string;
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly action: RuntimeExecutionIntentAction;
  readonly targetId: string | null;
  readonly dispatchIdentity: CommandId;
  readonly signal: AbortSignal | undefined;
  readonly finalAttempt: boolean;
  readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
}): Promise<RuntimeExecutionIntentData | null> {
  try {
    return await input.request<RuntimeExecutionIntentData>({
      method: "POST",
      path: executionIntentPath(input.runtimeId),
      body: {
        environmentId: input.environmentId,
        projectId: input.projectId,
        action: input.action,
        targetId: input.targetId,
        idempotencyKey: input.dispatchIdentity,
        dispatchIdentity: input.dispatchIdentity,
      },
      signal: input.signal,
    });
  } catch (error) {
    if (!isRetryableRequestError(error) || input.finalAttempt) throw error;
    input.onWaiting?.(
      error instanceof Error ? error.message : "Waiting for the environment to become ready.",
      error instanceof RuntimeExecutionIntentError ? error.code : null,
    );
    return null;
  }
}

async function resolveExecutionIntentAttempt(input: {
  readonly data: RuntimeExecutionIntentData;
}): Promise<RuntimeExecutionIntentData | null> {
  if (input.data.intent.status === "ready" && input.data.connection) {
    return input.data;
  }
  if (input.data.intent.status === "dispatched") {
    return input.data;
  }
  if (input.data.intent.status === "rejected") {
    throw new Error(terminalIntentMessage(input.data));
  }
  return null;
}

export async function prepareRuntimeExecutionIntent(
  input: {
    readonly runtimeId: string;
    readonly environmentId: EnvironmentId;
    readonly projectId: number | null;
    readonly action: RuntimeExecutionIntentAction;
    readonly targetId: string | null;
    readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  },
  dependencies: RuntimeExecutionIntentRequestDeps = { request: defaultRequest },
): Promise<RuntimeExecutionIntentData> {
  const request = dependencies.request;
  const sleep = dependencies.sleep ?? defaultSleep;
  for (let attempt = 1; attempt <= PREPARE_MAX_ATTEMPTS; attempt += 1) {
    input.signal?.throwIfAborted();
    const data = await requestExecutionIntentAttempt({
      request,
      ...input,
      signal: input.signal,
      finalAttempt: attempt === PREPARE_MAX_ATTEMPTS,
    });
    input.signal?.throwIfAborted();
    const resolved = data ? await resolveExecutionIntentAttempt({ data }) : null;
    if (resolved) return resolved;
    if (attempt < PREPARE_MAX_ATTEMPTS) await sleep(PREPARE_RETRY_DELAY_MS, input.signal);
  }
  throw new Error("This Environment did not become ready within 5 minutes.");
}

export async function markRuntimeExecutionIntentDispatched(
  input: {
    readonly runtimeId: string;
    readonly intentId: string;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  },
  dependencies: RuntimeExecutionIntentRequestDeps = { request: defaultRequest },
): Promise<RuntimeExecutionIntentDispatchData> {
  const sleep = dependencies.sleep ?? defaultSleep;
  for (let attempt = 1; attempt <= ACKNOWLEDGE_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await dependencies.request<RuntimeExecutionIntentDispatchData>({
        method: "POST",
        path: executionIntentDispatchPath(input),
        body: { dispatchIdentity: input.dispatchIdentity },
        signal: input.signal,
      });
    } catch (error) {
      if (!isRetryableRequestError(error) || attempt === ACKNOWLEDGE_MAX_ATTEMPTS) throw error;
      await sleep(PREPARE_RETRY_DELAY_MS, input.signal);
    }
  }
  throw new Error("Forge could not acknowledge this Environment action.");
}
